import { yupResolver } from '@hookform/resolvers/yup';
import { Form, message, Modal } from 'antd';
import { useCreateLocationMutation } from 'gql/types';
import { FormItem, Spin } from 'modules/common/components';
import { FC, MouseEvent, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getFormFields } from './fields';
import { IAddLocationsFormSchema, addLocationsFormSchema } from './schema';

import { BaseModalProps } from '../../../common/types';

const CreateLocationModal: FC<BaseModalProps> = ({
  visible,
  onOk,
  onCancel,
  hide,
  ...restModalProps
}) => {
  const { t } = useTranslation('user.CreateLocationsForm');
  const { control, formState, handleSubmit, reset } =
    useForm<IAddLocationsFormSchema>({
      resolver: yupResolver(addLocationsFormSchema),
      reValidateMode: 'onChange',
      mode: 'onChange',
      defaultValues: {
        name: undefined,
        type: undefined,
      },
    });
  const [createLocation, createLocationMutation] = useCreateLocationMutation({
    update: (cache) => {
      cache.evict({ fieldName: 'getLocations' });
      cache.gc();
    },
    refetchQueries: ['getLocations'],
    onCompleted: () => {
      message.success(t('locationCreated'));
      hide();
      reset();
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
  const formFields = useMemo(() => {
    return getFormFields({ control });
  }, []);

  const isOkButtonDisabled =
    !formState.isValid || createLocationMutation.loading;

  const submitForm = handleSubmit((formData) => {
    createLocation({
      variables: {
        input: formData,
      },
    });
  });

  const cancelHandler = (e: MouseEvent<HTMLElement>) => {
    onCancel && onCancel(e);
    reset();
    hide();
  };

  const okHandler = async (e: MouseEvent<HTMLElement>) => {
    await submitForm();
    onOk && onOk(e);
  };

  return (
    <Modal
      {...restModalProps}
      title={t('createLocation')}
      visible={visible}
      onOk={okHandler}
      onCancel={cancelHandler}
      okText={t('create')}
      okButtonProps={{
        disabled: isOkButtonDisabled,
      }}
    >
      <Spin spinning={createLocationMutation.loading}>
        <Form>
          {formFields.map(({ name, Component, getErrorMessage }) => {
            const errorMessage = getErrorMessage(formState.errors);
            const validateStatus = errorMessage && 'error';
            return (
              <FormItem
                key={name}
                extra={errorMessage}
                validateStatus={validateStatus}
              >
                <Component />
              </FormItem>
            );
          })}
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateLocationModal;
